import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { omit } from "ramda";
import { validate } from "validate.js";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { fade, makeStyles } from "@material-ui/core/styles";
import grey from "@material-ui/core/colors/grey";

import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import StarIcon from "@material-ui/icons/Star";

import {
  addExternalLink,
  removeExternalLink,
  updateExternalLink,
} from "file-details/fileDetailsActions";
import { getExternalLinks } from "common/selectors";
import IntlDialogActions from "general-components/IntlDialogActions";
import IntlInputLabel from "general-components/IntlInputLabel";
import IntlSelect from "general-components/IntlSelect";
import IntlTextField from "general-components/IntlTextField";
import { getFieldOptions, getProjectId } from "../../common/selectors";
import { parseFieldLabel } from "../../common/fieldOptionsUtils";

export const AWARD = "AWARD";
export const REVIEW = "REVIEW";

// Links may arrive with either `linkType` or `type`, in any case
const linkTypeOf = link => (link?.linkType ?? link?.type)?.toUpperCase();

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  linksContainer: {
    maxWidth: 600,
    borderBottomStyle: "dashed",
    borderBottomColor: grey[300],
  },

  linkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "2px 5px",
    borderRadius: 5,
    "&:hover": {
      backgroundColor: grey[100],
    },
  },

  linkTitle: {
    color: theme.palette.secondary.main,
    fontWeight: 800,
    fontSize: "1.13rem",
    display: "inline-block",
    alignItems: "center",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: 300,
  },

  linkTitleContainer: {
    display: "flex",
    alignItems: "center",
  },

  linkIcon: {
    fontSize: "1rem",
    color: theme.palette.secondary.main,
    fontWeight: 800,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },

  linkActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },

  chip: {
    width: 100,
    fontWeight: theme.typography.fontWeightBold,
  },

  awardChip: {
    backgroundColor: fade(theme.palette.secondary.main, 0.12),
    color: theme.palette.secondary.dark,
    "& .MuiChip-icon": {
      color: theme.palette.secondary.dark,
    },
  },

  reviewChip: {
    backgroundColor: theme.palette.primary.green.light,
    color: theme.palette.primary.green.dark,
    "& .MuiChip-icon": {
      color: theme.palette.primary.green.dark,
    },
  },

  addLinkButton: {
    textTransform: "uppercase",
    fontWeight: "bolder",
    marginTop: theme.spacing(1),
  },

  startIconRtl: {
    marginLeft: 8,
    marginRight: "-4px",
  },

  dialogTitle: {
    paddingRight: theme.spacing(7),
  },

  dialogTitleRtl: {
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(7),
  },

  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },

  closeButtonRtl: {
    right: "auto",
    left: theme.spacing(1),
  },
}));

const ExternalLinks = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const dispatch = useDispatch();
  const externalLinks = useSelector(getExternalLinks) ?? [];
  const fieldOptions = useSelector(getFieldOptions);
  const projectId = useSelector(getProjectId);
  const [editing, setEditing] = useState(null);

  const close = () => setEditing(null);

  const save = link => {
    const normalized = { ...omit(["type"], link), linkType: linkTypeOf(link) };
    dispatch(
      editing.index === null
        ? addExternalLink(normalized)
        : updateExternalLink(editing.index, normalized),
    );
    close();
  };

  const typeLabel = linkType =>
    linkType === REVIEW
      ? t("external_links.review", "Review")
      : t("external_links.award", "Award");

  // Providing a custom field label requires adding a translation key
  // with the corresponding project id
  const fieldLabel = parseFieldLabel(fieldOptions?.external_links?.fieldLabel);
  const heading =
    typeof fieldLabel === "string"
      ? fieldLabel
      : fieldLabel?.header
      ? t(`external_links.${projectId}.header`, fieldLabel?.header)
      : t("external_links.header", "Awards & Reviews");

  const subheading = fieldLabel?.header_sub
    ? t(`external_links.${projectId}.header_sub`, fieldLabel?.header_sub)
    : t(
        "external_links.header_sub",
        "Has your work won an award or been reviewed? Add links here.",
      );

  return (
    <Fragment>
      <div className={classes.sectionHeader}>
        <Typography variant="h2">
          {heading} <Typography variant="body2">{subheading}</Typography>
        </Typography>
      </div>

      <div className={classes.linksContainer}>
        {externalLinks.map((link, index) => {
          const linkType = linkTypeOf(link);
          return (
            <div
              id={`external-link-${index}`}
              // Titles aren't unique, so the index keeps keys distinct
              key={link.externalLinkId ?? `${index}-${link.title}`}
              className={classes.linkRow}
            >
              <div className={classes.linkTitleContainer}>
                <Link
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.linkTitle}
                >
                  {link.title}
                </Link>
                <OpenInNewIcon className={classes.linkIcon} />
              </div>

              <div className={classes.linkActions}>
                <Chip
                  size="small"
                  className={clsx(
                    classes.chip,
                    linkType === REVIEW
                      ? classes.reviewChip
                      : classes.awardChip,
                  )}
                  icon={
                    linkType === REVIEW ? <StarIcon /> : <EmojiEventsIcon />
                  }
                  label={typeLabel(linkType)}
                />
                <IconButton
                  aria-label={t("external_links.remove_aria", {
                    defaultValue: "Remove {{title}} link",
                    title: link.title,
                  })}
                  onClick={() => dispatch(removeExternalLink(index))}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  aria-label={t("external_links.edit_aria", {
                    defaultValue: "Edit {{title}} link",
                    title: link.title,
                  })}
                  onClick={() => setEditing({ index, link })}
                >
                  <EditIcon />
                </IconButton>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <Button
          classes={{
            label: classes.addLinkButton,
            startIcon: clsx({ [classes.startIconRtl]: isRtl }),
          }}
          variant="text"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() =>
            setEditing({
              index: null,
              link: { title: "", url: "", linkType: AWARD },
            })
          }
        >
          {t("external_links.add_link", "Add Link")}
        </Button>
      </div>

      {editing?.link && (
        <ExternalLinkDialog
          initialLink={editing.link}
          title={
            editing.index === null
              ? t("external_links.add_link", "Add Link")
              : t("external_links.edit_link", "Edit Link")
          }
          buttonLabel={
            editing.index === null
              ? t("external_links.add", "Add")
              : t("external_links.update", "Update")
          }
          onClose={close}
          onSave={save}
        />
      )}
    </Fragment>
  );
};

const createConstraints = t => ({
  title: {
    presence: {
      message: t(
        "external_links.validation_title_required",
        "Display text cannot be blank",
      ),
      allowEmpty: false,
    },
  },

  url: {
    presence: {
      message: t(
        "external_links.validation_url_required",
        "URL cannot be blank",
      ),
      allowEmpty: false,
    },
    url: {
      message: value =>
        t("external_links.validation_url_invalid", {
          defaultValue: "'{{value}}' is not a valid URL",
          value,
        }),
    },
  },
});

const ExternalLinkDialog = ({
  initialLink,
  onClose,
  onSave,
  title,
  buttonLabel,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const constraints = createConstraints(t);
  const [errors, setErrors] = useState(null);
  const [link, setLink] = useState(initialLink);

  const onBlur = prop => _event =>
    setErrors(prev => ({
      ...prev,
      [prop]: validate.single(link[prop], constraints[prop], {
        fullMessages: false,
      }),
    }));

  const onChange = prop => event => {
    const value = event.target.value;
    setLink(prev => ({ ...prev, [prop]: value }));
  };

  const save = () => {
    const errors = validate(link, constraints, { fullMessages: false });
    if (errors) {
      setErrors(errors);
      return;
    }
    onSave(link);
  };

  return (
    <Dialog open onClose={onClose} aria-labelledby="external-link-dialog-title">
      <DialogTitle
        disableTypography
        className={clsx(classes.dialogTitle, {
          [classes.dialogTitleRtl]: isRtl,
        })}
      >
        <Typography id="external-link-dialog-title" component="h2" variant="h6">
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={t("external_links.close_dialog", "Close dialog")}
          className={clsx(classes.closeButton, {
            [classes.closeButtonRtl]: isRtl,
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <IntlTextField
              required
              fullWidth
              id="link-title"
              name="link-title"
              label={t("external_links.display_text", "Display text")}
              value={link.title}
              onBlur={onBlur("title")}
              onChange={onChange("title")}
              error={Boolean(errors?.title)}
              helperText={errors?.title?.[0]}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl required fullWidth>
              <IntlInputLabel id="link-type-label">
                {t("external_links.type", "Type")}
              </IntlInputLabel>
              <IntlSelect
                labelId="link-type-label"
                id="link-type-select"
                value={linkTypeOf(link) ?? AWARD}
                onChange={onChange("linkType")}
              >
                <MenuItem value={AWARD}>
                  {t("external_links.award", "Award")}
                </MenuItem>
                <MenuItem value={REVIEW}>
                  {t("external_links.review", "Review")}
                </MenuItem>
              </IntlSelect>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <IntlTextField
              required
              fullWidth
              id="link-url"
              name="link-url"
              label={t("external_links.url", "URL")}
              placeholder="https://..."
              value={link.url}
              onBlur={onBlur("url")}
              onChange={onChange("url")}
              error={Boolean(errors?.url)}
              helperText={errors?.url?.[0]}
              inputProps={{ maxLength: 2048 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <IntlDialogActions>
        <Button onClick={onClose}>{t("app_cancel", "Cancel")}</Button>
        <Button variant="contained" color="secondary" onClick={save}>
          {buttonLabel}
        </Button>
      </IntlDialogActions>
    </Dialog>
  );
};

export default ExternalLinks;
